"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import { SIDEBAR_COLLAPSED_COOKIE } from "@/lib/layout/sidebar";

type Viewport = "mobile" | "narrow" | "wide";

type SidebarState = {
  collapsed: boolean;
  toggle: () => void;
};

const SidebarStateContext = createContext<SidebarState | null>(null);

function persistCollapsed(collapsed: boolean) {
  document.cookie = `${SIDEBAR_COLLAPSED_COOKIE}=${collapsed ? "1" : "0"}; path=/; max-age=31536000; samesite=lax`;
}

function getViewport(): Viewport {
  if (window.matchMedia("(max-width: 767px)").matches) {
    return "mobile";
  }
  if (window.matchMedia("(max-width: 1279px)").matches) {
    return "narrow";
  }
  return "wide";
}

function subscribeToViewport(onStoreChange: () => void) {
  const mobile = window.matchMedia("(max-width: 767px)");
  const narrow = window.matchMedia("(max-width: 1279px)");
  mobile.addEventListener("change", onStoreChange);
  narrow.addEventListener("change", onStoreChange);
  return () => {
    mobile.removeEventListener("change", onStoreChange);
    narrow.removeEventListener("change", onStoreChange);
  };
}

function getServerSnapshot(): Viewport {
  return "wide";
}

export function SidebarStateProvider({
  initialCollapsed,
  children,
}: {
  initialCollapsed: boolean;
  children: ReactNode;
}) {
  const viewport = useSyncExternalStore(
    subscribeToViewport,
    getViewport,
    getServerSnapshot,
  );
  const [userCollapsed, setUserCollapsed] = useState(initialCollapsed);
  const [narrowOpen, setNarrowOpen] = useState(false);

  useEffect(() => {
    if (viewport === "narrow") {
      setNarrowOpen(false);
    }
  }, [viewport]);

  const collapsed =
    viewport === "mobile"
      ? false
      : viewport === "narrow"
        ? !narrowOpen
        : userCollapsed;

  const toggle = useCallback(() => {
    if (viewport === "narrow") {
      setNarrowOpen((current) => !current);
      return;
    }

    setUserCollapsed((current) => {
      const next = !current;
      persistCollapsed(next);
      return next;
    });
  }, [viewport]);

  const value = useMemo(() => ({ collapsed, toggle }), [collapsed, toggle]);

  return (
    <SidebarStateContext.Provider value={value}>
      {children}
    </SidebarStateContext.Provider>
  );
}

export function useSidebarState() {
  const context = useContext(SidebarStateContext);
  if (!context) {
    throw new Error("useSidebarState a SidebarStateProvideren belül használható.");
  }
  return context;
}

export function useSidebarCollapsed() {
  return useContext(SidebarStateContext)?.collapsed ?? false;
}
